import { FaArrowRight, FaUserCheck, FaBuildingColumns } from "react-icons/fa6";
import { IMAGES, WHATSAPP_LINK } from "@/data/content";

const Hero = () => {
  const scrollTo = (href) => {
    if (window.lenis) {
      window.lenis.scrollTo(href, { duration: 1.0 });
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="noise mesh-gold relative flex min-h-[100svh] items-center overflow-hidden bg-white pb-16 pt-28 md:pt-32"
    >
      {/* floating background accents */}
      <div className="pointer-events-none absolute -left-20 top-24 h-[360px] w-[360px] animate-floatY rounded-full bg-gold-soft/40 blur-[70px] will-change-transform" />
      <div
        className="pointer-events-none absolute -right-10 bottom-0 h-[380px] w-[380px] animate-floatY rounded-full bg-gold-light blur-[70px] will-change-transform"
        style={{ animationDelay: "2s" }}
      />

      {/* particles */}
      {[...Array(14)].map((_, i) => (
        <span
          key={i}
          className="pointer-events-none absolute h-1 w-1 animate-floatY rounded-full bg-gold-primary/60"
          style={{
            left: `${(i * 37) % 100}%`,
            top: `${(i * 53) % 100}%`,
            animationDelay: `${i * 0.3}s`,
            animationDuration: `${4 + (i % 5)}s`,
          }}
        />
      ))}

      <div className="relative z-10 mx-auto grid w-full max-w-[1300px] grid-cols-1 items-center gap-12 px-6 md:px-10 lg:grid-cols-12">
        {/* Left: copy */}
        <div className="lg:col-span-7">
          <div
            className="hero-fade mb-6 inline-flex items-center gap-2 rounded-full border border-gold-soft bg-gold-light px-4 py-2 text-[11px] font-bold uppercase tracking-[0.2em] text-green-primary shadow-soft"
            style={{ animationDelay: "1.6s" }}
          >
            <span className="h-2 w-2 rounded-full bg-gold-primary animate-pulse" />
            PLACE8 Recruitment Consultancy
          </div>

          <h1 className="font-display flex flex-col gap-0.5 text-5xl font-extralight leading-[1.02] tracking-tightest text-green-primary sm:text-7xl lg:text-[5.4rem]">
            <span className="sr-only">PLACE8 Talent | Recruitment Consultancy in Navi Mumbai</span>
            <span className="reveal-mask block">
              <span
                className="hero-line block"
                style={{ animationDelay: "1.7s" }}
              >
                Premium
              </span>
            </span>
            <span className="reveal-mask block">
              <span
                className="hero-line block italic font-light text-gradient-gold py-1"
                style={{ animationDelay: "1.81s" }}
              >
                Recruitment
              </span>
            </span>
            <span className="reveal-mask block">
              <span
                className="hero-line block"
                style={{ animationDelay: "1.92s" }}
              >
                Solutions
              </span>
            </span>
          </h1>

          <p
            className="hero-fade mt-6 max-w-xl text-base leading-relaxed text-green-primary md:text-lg font-medium"
            style={{ animationDelay: "2.2s" }}
          >
            Helping businesses hire exceptional talent through executive search,
            permanent hiring, bulk recruitment, staffing solutions, and
            industry-specific hiring across India.
          </p>

          <div
            className="hero-fade mt-9 flex flex-wrap items-center gap-4"
            style={{ animationDelay: "2.4s" }}
          >
            <button
              data-testid="hero-hire-talent"
              onClick={() => scrollTo("#contact")}
              className="btn-gold group flex items-center gap-3 rounded-full px-8 py-4 text-xs md:text-sm font-bold uppercase tracking-[0.1em] text-white transition-transform duration-300 hover:-translate-y-1 shadow-luxe"
            >
              Hire Talent
              <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              data-testid="hero-book-consultation"
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary-green group flex items-center gap-3 rounded-full px-8 py-4 text-xs md:text-sm font-bold uppercase tracking-[0.1em] shadow-soft transition-all duration-300 hover:-translate-y-0.5"
            >
              Book Consultation
              <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </div>
        </div>

        {/* Right: image + cards */}
        <div className="relative lg:col-span-5">
          <div
            className="hero-zoom relative overflow-hidden rounded-3xl shadow-luxe border border-gold-soft bg-white"
            style={{ animationDelay: "1.9s" }}
          >
            <img
              src={IMAGES.hero}
              alt="PLACE8 Executive Recruitment Consultancy — HR Leaders and Business Meeting"
              loading="eager"
              fetchpriority="high"
              decoding="async"
              width="900"
              height="1120"
              className="h-[440px] w-full object-cover md:h-[540px] transition-transform duration-700 hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-green-primary/30 via-transparent to-transparent" />
          </div>

          <div
            className="hero-fade bg-white/95 backdrop-blur-md absolute -left-4 top-4 md:top-5 flex animate-floatY items-center gap-3 rounded-2xl px-5 py-4 shadow-luxe md:-left-10 z-20 border border-gold-soft"
            style={{ animationDelay: "2.6s" }}
          >
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-green-primary text-gold-primary shadow-md">
              <FaUserCheck className="text-lg" />
            </span>
            <div>
              <p className="text-xl font-bold leading-none text-green-primary">5000+</p>
              <p className="mt-1 text-[11px] font-bold uppercase tracking-widest text-gold-primary">
                Candidates Placed
              </p>
            </div>
          </div>

          <div
            className="hero-fade bg-white/95 backdrop-blur-md absolute bottom-2 md:bottom-4 right-0 flex animate-floatY items-center gap-3 rounded-2xl px-5 py-4 shadow-luxe md:-right-8 z-20 border border-gold-soft"
            style={{ animationDelay: "2.8s" }}
          >
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-gold-primary text-white shadow-md">
              <FaBuildingColumns className="text-lg" />
            </span>
            <div>
              <p className="text-xl font-bold leading-none text-green-primary">55+</p>
              <p className="mt-1 text-[11px] font-bold uppercase tracking-widest text-gold-primary">
                Hiring Partners
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
